import { useState } from "react";

/* ─────────────────────────────────────────
   AI TRACK PAGE  — مسار الذكاء الاصطناعي
   Brand accent: Ember #D85A30
   ───────────────────────────────────────── */

const EMBER = "#D85A30";
const MINT = "#6DD3A7";
const GOLD = "#F2B949";

/* ── reusable small bits ── */
const Tag = ({ children }) => (
  <span className="inline-flex items-center gap-1 text-[.68rem] font-medium bg-[rgba(216,90,48,.08)] border border-[rgba(216,90,48,.15)] text-[#D85A30] px-[9px] py-[3px] rounded-full whitespace-nowrap">
    {children}
  </span>
);

const Check = ({ children }) => (
  <li className="flex items-start gap-3 text-[.88rem] text-[#94B4C8] leading-[1.7]">
    <span className="mt-[3px] w-[18px] h-[18px] rounded-full border border-[rgba(216,90,48,.4)] flex items-center justify-center flex-shrink-0">
      <svg className="w-[9px] h-[9px]" viewBox="0 0 10 8" fill="none">
        <polyline points="1 4 3.5 6.5 9 1" stroke="#D85A30" strokeWidth="1.8" />
      </svg>
    </span>
    <span>{children}</span>
  </li>
);

const StatBox = ({ n, label }) => (
  <div className="text-center">
    <span className="block font-cairo font-black text-[1.8rem] leading-none text-[#D85A30] mb-[3px]">
      {n}
    </span>
    <span className="block text-[.62rem] text-[#3D6278] tracking-[.08em] uppercase">
      {label}
    </span>
  </div>
);

/* ── Module card (accordion) ── */
function Module({ num, title, tools, outcomes, href, open, onToggle }) {
  return (
    <div
      className={`rounded-[12px] border overflow-hidden transition-all duration-300 ${
        open
          ? "border-[rgba(216,90,48,.3)] bg-[#0C1F35]"
          : "border-[rgba(109,211,167,.09)] bg-[#091828]"
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-[clamp(16px,2vw,24px)] py-[clamp(14px,1.8vh,20px)] text-right"
      >
        {/* step */}
        <span
          className="flex-shrink-0 w-[36px] h-[36px] rounded-full flex items-center justify-center font-mono text-[.58rem] font-bold text-[#0E2A47]"
          style={{
            background: open ? EMBER : "rgba(216,90,48,.15)",
            color: open ? "#fff" : EMBER,
          }}
        >
          {String(num).padStart(2, "0")}
        </span>
        <span className="flex-1 font-cairo font-bold text-[clamp(.9rem,1.1vw,1.05rem)] text-[#F7F2E9] text-right leading-[1.3]">
          {title}
        </span>
        <svg
          className={`w-4 h-4 text-[#3D6278] transition-transform duration-300 flex-shrink-0 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="px-[clamp(16px,2vw,24px)] pb-[clamp(16px,2vh,22px)]">
          {/* top accent */}
          <div className="h-px bg-[rgba(216,90,48,.15)] mb-[16px]" />

          <div className="grid gap-5 md:grid-cols-2">
            {/* tools */}
            <div>
              <p className="font-mono text-[.5rem] tracking-[.14em] uppercase text-[#3D6278] mb-[8px]">
                الأدوات والمفاهيم
              </p>
              <div className="flex flex-wrap gap-1.5">
                {tools.map((t) => (
                  <span
                    key={t}
                    className="text-[.66rem] bg-[rgba(216,90,48,.06)] border border-[rgba(216,90,48,.12)] text-[#D85A30] px-[8px] py-[2px] rounded-full"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
            {/* outcomes */}
            <div>
              <p className="font-mono text-[.5rem] tracking-[.14em] uppercase text-[#3D6278] mb-[8px]">
                ماذا ستنجز
              </p>
              <ul className="space-y-[6px]">
                {outcomes.map((o) => (
                  <li
                    key={o}
                    className="flex items-start gap-2 text-[.76rem] text-[#94B4C8] leading-[1.6]"
                  >
                    <span className="text-[#D85A30] mt-[2px] flex-shrink-0">
                      ◆
                    </span>
                    {o}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {href && (
            <a
              href={href}
              target="_blank"
              rel="noopener"
              className="mt-[16px] inline-flex items-center gap-2 font-cairo font-bold text-[.82rem] text-[#D85A30] bg-[rgba(216,90,48,.07)] border border-[rgba(216,90,48,.18)] px-[16px] py-[8px] rounded-[4px] transition-all duration-200 hover:bg-[#D85A30] hover:text-white hover:border-[#D85A30]"
            >
              <svg
                className="w-3.5 h-3.5 flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
              سجّل في هذه المحطة
            </a>
          )}
        </div>
      )}
    </div>
  );
}

/* ── FAQ item ── */
function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`rounded-[10px] border overflow-hidden transition-all duration-200 ${open ? "border-[rgba(216,90,48,.25)] bg-[#0C1F35]" : "border-[rgba(109,211,167,.09)] bg-[#091828]"}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-[20px] py-[16px] text-right"
      >
        <span className="font-cairo font-bold text-[clamp(.86rem,.95rem,1rem)] text-[#F7F2E9] leading-[1.3]">
          {q}
        </span>
        <span
          className={`w-[26px] h-[26px] rounded-full border flex items-center justify-center flex-shrink-0 transition-all duration-200 ${open ? "border-[#D85A30] bg-[rgba(216,90,48,.1)]" : "border-[rgba(109,211,167,.2)]"}`}
        >
          <svg
            className={`w-[11px] h-[11px] stroke-[#D85A30] transition-transform duration-300 ${open ? "rotate-45" : ""}`}
            viewBox="0 0 24 24"
            fill="none"
            strokeWidth="2.5"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="px-[20px] pb-[16px] border-t border-[rgba(216,90,48,.08)]">
          <p
            className="pt-[12px] text-[.85rem] text-[#94B4C8] leading-[1.85]"
            dangerouslySetInnerHTML={{ __html: a }}
          />
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   PAGE DATA
══════════════════════════════════════════ */

const MODULES = [
  {
    num: 1,
    title: "تحليل البيانات بواسطة AI للوصول إلى قرارات",
    href: "https://store.afaaqwetaan.com/%D8%AA%D8%AD%D9%84%D9%8A%D9%84-%D8%A7%D9%84%D8%A8%D9%8A%D8%A7%D9%86%D8%A7%D8%AA-%D8%A8%D9%88%D8%A7%D8%B3%D8%B7%D8%A9-ai-%D9%84%D9%84%D9%88%D8%B5%D9%88%D9%84-%D8%A5%D9%84%D9%89-%D9%82%D8%B1%D8%A7%D8%B1%D8%A7%D8%AA/p845652276",
    tools: [
      "Python",
      "Pandas",
      "Matplotlib",
      "NumPy",
      "EDA",
      "Jupyter",
      "Power BI",
    ],
    outcomes: [
      "تنظيف وتحليل مجموعات بيانات حقيقية",
      "بناء لوحة تحليلية تفاعلية",
      "استخلاص رؤى قابلة للتطبيق من البيانات",
    ],
  },
  {
    num: 2,
    title: "تعلم الآلة وبناء النماذج والتعامل معها",
    href: "https://store.afaaqwetaan.com/%D8%AA%D8%B9%D9%84%D9%85-%D8%A7%D9%84%D8%A2%D9%84%D8%A9-%D9%88%D8%A8%D9%86%D8%A7%D8%A1-%D8%A7%D9%84%D9%86%D9%85%D8%A7%D8%B0%D8%AC-%D9%88%D8%A7%D9%84%D8%AA%D8%B9%D8%A7%D9%85%D9%84-%D9%85%D8%B9%D9%87%D8%A7/p1075683787",
    tools: [
      "Scikit-learn",
      "XGBoost",
      "Random Forest",
      "Optuna",
      "SHAP",
      "Regression",
      "Classification",
    ],
    outcomes: [
      "بناء نموذج تنبؤي وضبط معاملاته",
      "تفسير النتائج لأصحاب القرار",
      "تقييم النموذج وتحسين دقته",
    ],
  },
  {
    num: 3,
    title: "معالجة اللغة الطبيعية وبناء نماذج NLP",
    href: "https://store.afaaqwetaan.com/%D9%85%D8%B9%D8%A7%D9%84%D8%AC%D8%A9-%D8%A7%D9%84%D9%84%D8%BA%D8%A9-%D8%A7%D9%84%D8%B7%D8%A8%D9%8A%D8%B9%D9%8A%D8%A9-%D9%88%D8%A8%D9%86%D8%A7%D8%A1-%D9%86%D9%85%D8%A7%D8%B0%D8%AC-nlp/p1766138575",
    tools: [
      "HuggingFace",
      "BERT",
      "AraBERT",
      "Fine-Tuning",
      "Gradio",
      "FastAPI",
      "Transformers",
    ],
    outcomes: [
      "بناء نموذج تحليل مشاعر للمحتوى العربي",
      "نشر النموذج كـ API جاهز للاستخدام",
      "دمج الـ LLMs في تطبيق عملي",
    ],
  },
  {
    num: 4,
    title: "ربط الذكاء الاصطناعي بالأعمال",
    href: "https://store.afaaqwetaan.com/%D8%B1%D8%A8%D8%B7-%D8%A7%D9%84%D8%B0%D9%83%D8%A7%D8%A1-%D8%A7%D9%84%D8%A7%D8%B5%D8%B7%D9%86%D8%A7%D8%B9%D9%8A-%D8%A8%D8%A7%D9%84%D8%A3%D8%B9%D9%85%D8%A7%D9%84/p959116254",
    tools: [
      "Power BI",
      "Tableau",
      "Streamlit",
      "Business KPIs",
      "ROI Analysis",
      "Storytelling",
    ],
    outcomes: [
      "ترجمة نتائج النماذج إلى قرارات تجارية",
      "بناء تقرير تنفيذي احترافي",
      "ربط مخرجات AI بـ KPIs الإدارة",
    ],
  },
  {
    num: 5,
    title: "بناء وتطوير تطبيقات الذكاء الاصطناعي",
    href: "https://store.afaaqwetaan.com/%D8%A8%D9%86%D8%A7%D8%A1-%D9%88%D8%AA%D8%B7%D9%88%D9%8A%D8%B1-%D8%AA%D8%B7%D8%A8%D9%8A%D9%82%D8%A7%D8%AA-%D8%A7%D9%84%D8%B0%D9%83%D8%A7%D8%A1-%D8%A7%D9%84%D8%A7%D8%B5%D8%B7%D9%86%D8%A7%D8%B9%D9%8A/p634919463",
    tools: [
      "GitHub Actions",
      "Docker",
      "CI/CD",
      "Frontend UI",
      "Project Architecture",
      "Deployment",
      "Portfolio",
    ],
    outcomes: [
      "تصميم بنية مشروع AI متكاملة",
      "نشر التطبيق على الـ Cloud",
      "مشروع تخرج قابل للإضافة للـ LinkedIn",
    ],
  },
];

const FOR_WHO = [
  {
    icon: "🎓",
    title: "خريجو التخصصات التقنية",
    desc: "برمجة، نظم معلومات، إحصاء، رياضيات — تريد تترجم دراستك لمشاريع حقيقية.",
  },
  {
    icon: "💼",
    title: "الموظف الباحث عن التحول",
    desc: "تشتغل في الأعمال أو التسويق أو المالية وتريد تُدخل AI في عملك اليومي.",
  },
  {
    icon: "🌱",
    title: "المبتدئ الجاد",
    desc: "ما عندك خلفية برمجية عميقة لكن عندك رغبة حقيقية في التعلم والالتزام اليومي.",
  },
];

const PROJECTS = [
  {
    code: "GFI",
    name: "قرينفاي",
    desc: "منصة تمويل خضراء تستخدم AI لتقييم المشاريع المستدامة.",
  },
  {
    code: "RFQ",
    name: "رفيق",
    desc: "مساعد ذكي يرافق الخريجين في مسيرتهم المهنية بتوصيات مخصصة.",
  },
  {
    code: "MDD",
    name: "مداد",
    desc: "أداة AI لتوليد المحتوى العربي وتحليله للمؤسسات.",
  },
  {
    code: "AIM",
    name: "AIM",
    desc: "منصة لدمج AI في العمليات التجارية وتحليل سير العمل.",
  },
];

const FAQS = [
  {
    q: "ما المستوى المطلوب قبل البدء؟",
    a: "تعرف تشتغل على جهاز كمبيوتر وتفهم أساسيات Excel أو Python. ما تحتاج تكون مبرمجاً محترفاً — كل محطة تبدأ من المبادئ الأساسية لذلك الموضوع.",
  },
  {
    q: "كيف يختلف هذا المسار عن الدورات المجانية على يوتيوب؟",
    a: "الدورات المجانية تعلّمك المفاهيم. آفاق تبني معك مشروعاً حقيقياً يُضاف لملفك المهني، وتضعك في بيئة تدريبية تُحاكي العمل الفعلي مع لقاءات أسبوعية مع خبراء السوق.",
  },
  {
    q: "هل يمكنني أخذ محطة واحدة فقط؟",
    a: "نعم. كل محطة متاحة للتسجيل بشكل مستقل بـ ١٣٥٠ ر. لكن إذا أردت الاستفادة الكاملة من الشهادة ومشروع التخرج، تحتاج إلى إتمام المحطات الخمس.",
  },
  {
    q: "ما جدول التدريب اليومي؟",
    a: "الجلسات الحية: ٩ صباحاً – ٣ عصراً (الأحد–الخميس). لقاء إثرائي أسبوعي مع خبراء: ٧–٩ مساءً. التسجيلات متاحة بعد كل جلسة. تحتاج من ٦ إلى ٨ ساعات يومياً للاستفادة القصوى.",
  },
  {
    q: "كيف أحصل على شهادة الإتمام؟",
    a: "الشهادة مشروطة بـ: <strong>١)</strong> حضور أكثر من ٧٠٪ من الجلسات <strong>٢)</strong> إتمام كل التاسكات الأسبوعية <strong>٣)</strong> تقديم مشروع التخرج أمام لجنة التقييم.",
  },
  {
    q: "هل الدفع بالتقسيط متاح؟",
    a: "نعم — الدفع بالتقسيط عبر <strong>تمارا</strong> متاح مباشرة من صفحة المنتج في متجر آفاق على سلة. لا حاجة لأي إجراء إضافي.",
  },
];

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
export default function AITrackPage() {
  const [openMod, setOpenMod] = useState(0);

  return (
    <div
      className="min-h-screen bg-[#081828] text-[#F7F2E9] font-['IBM_Plex_Sans_Arabic',sans-serif]"
      dir="rtl"
    >
      {/* ══ HERO ══════════════════════════════════════ */}
      <section className="relative overflow-hidden pt-[var(--nav-h,64px)]">
        {/* bg layers */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 80% 50% at 50% -10%,rgba(216,90,48,0.12) 0%,transparent 65%)",
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 60% 40% at 0% 60%,rgba(14,42,71,0.8) 0%,transparent 70%)",
            }}
          />
          {/* grid pattern */}
          <svg
            className="absolute inset-0 w-full h-full opacity-[.04]"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <pattern
                id="grid-ai"
                width="40"
                height="40"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 40 0 L 0 0 0 40"
                  fill="none"
                  stroke="#6DD3A7"
                  strokeWidth="0.5"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-ai)" />
          </svg>
        </div>

        <div className="relative z-10 px-[clamp(16px,5vw,80px)] py-[clamp(48px,8vh,96px)]">
          {/* breadcrumb */}
          <div className="flex items-center gap-2 mb-[clamp(24px,3vh,40px)]">
            <a
              href="/"
              className="font-mono text-[.58rem] tracking-[.12em] uppercase text-[#3D6278] hover:text-[#94B4C8] transition-colors duration-200"
            >
              الرئيسية
            </a>
            <span className="text-[#3D6278] text-[.5rem]">◆</span>
            <span className="font-mono text-[.58rem] tracking-[.12em] uppercase text-[#D85A30]">
              مسار الذكاء الاصطناعي
            </span>
          </div>

          <div className="grid gap-[clamp(32px,5vw,64px)] lg:grid-cols-[1fr_auto]">
            {/* text side */}
            <div className="max-w-[640px]">
              {/* eyebrow */}
              <div className="inline-flex items-center gap-2 mb-[clamp(16px,2vh,24px)]">
                <span
                  className="w-[8px] h-[8px] rounded-full animate-pulse"
                  style={{ background: EMBER }}
                />
                <span className="font-mono text-[.62rem] tracking-[.18em] uppercase text-[#D85A30] font-bold">
                  AI IN BUSINESS · مسار مكثّف
                </span>
              </div>

              <h1
                className="font-cairo font-black leading-[1.05] tracking-[-0.03em] text-[#F7F2E9] mb-[clamp(12px,1.5vh,20px)]"
                style={{ fontSize: "clamp(2.2rem,5vw,4.2rem)" }}
              >
                مسار
                <br />
                <em className="not-italic" style={{ color: EMBER }}>
                  الذكاء الاصطناعي
                </em>
                <br />
                في الأعمال
              </h1>

              <p className="text-[clamp(.9rem,.95rem,1.05rem)] text-[#94B4C8] leading-[1.9] mb-[clamp(20px,3vh,36px)] max-w-[500px]">
                ٥ محطات متسلسلة تأخذك من تحليل البيانات إلى بناء تطبيق AI حقيقي
                تُقدّمه أمام لجنة تقييم وتضيفه لملفك المهني.
              </p>

              {/* tags */}
              <div className="flex flex-wrap gap-2 mb-[clamp(24px,3.5vh,40px)]">
                {[
                  "Python & ML",
                  "NLP & AraBERT",
                  "Power BI",
                  "LLMs",
                  "Cloud Deployment",
                  "مشروع تخرج",
                ].map((t) => (
                  <Tag key={t}>{t}</Tag>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href="https://store.afaaqwetaan.com/%D8%A7%D9%84%D8%B0%D9%83%D8%A7%D8%A1-%D8%A7%D9%84%D8%A7%D8%B5%D8%B7%D9%86%D8%A7%D8%B9%D9%8A/c114156635"
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-2 font-cairo font-bold px-[28px] py-[13px] rounded-[4px] transition-all duration-200 hover:opacity-90 hover:-translate-y-[1px]"
                  style={{
                    background: EMBER,
                    color: "#fff",
                    fontSize: "clamp(.88rem,.95rem,1rem)",
                  }}
                >
                  <svg
                    className="w-4 h-4 flex-shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                  سجّل في المسار
                </a>
                <a
                  href="#curriculum"
                  className="inline-flex items-center gap-2 font-cairo font-medium px-[24px] py-[13px] rounded-[4px] border border-[rgba(247,242,233,.15)] text-[#94B4C8] transition-all duration-200 hover:border-[rgba(247,242,233,.3)] hover:text-[#F7F2E9]"
                  style={{ fontSize: "clamp(.86rem,.92rem,.96rem)" }}
                >
                  استعرض المحتوى
                </a>
              </div>
            </div>

            {/* stats card */}
            <div className="rounded-[16px] border border-[rgba(216,90,48,.2)] bg-[#0C1F35] p-[clamp(20px,2.5vw,32px)] self-start min-w-[220px]">
              <div
                className="h-[3px] rounded-full mb-[20px]"
                style={{
                  background: `linear-gradient(90deg,${EMBER},#E8733A)`,
                }}
              />
              <div className="grid grid-cols-2 gap-x-[16px] gap-y-[20px]">
                <StatBox n="١٠" label="أسابيع مكثفة" />
                <StatBox n="٣٠٠+" label="ساعة تدريب" />
                <StatBox n="٥" label="محطات متسلسلة" />
                <StatBox n="9.25" label="تقييم المتدربين" />
              </div>
              <div className="mt-[20px] pt-[16px] border-t border-[rgba(109,211,167,.07)]">
                <div className="flex items-center gap-2 mb-[6px]">
                  <span className="w-[6px] h-[6px] rounded-full bg-[#6DD3A7] flex-shrink-0" />
                  <span className="text-[.72rem] text-[#94B4C8]">
                    تدريب أونلاين مباشر + تسجيلات
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-[6px]">
                  <span className="w-[6px] h-[6px] rounded-full bg-[#6DD3A7] flex-shrink-0" />
                  <span className="text-[.72rem] text-[#94B4C8]">
                    شهادة إتمام معتمدة
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-[6px] h-[6px] rounded-full bg-[#F2B949] flex-shrink-0" />
                  <span className="text-[.72rem] text-[#94B4C8]">
                    دفع بالتقسيط عبر تمارا
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* bottom fade */}
        <div
          className="h-[60px]"
          style={{
            background: "linear-gradient(to bottom, transparent, #081828)",
          }}
        />
      </section>

      {/* ══ PILLARS ═══════════════════════════════════ */}
      <section className="px-[clamp(16px,5vw,80px)] py-[clamp(48px,7vh,80px)]">
        <div className="text-center mb-[clamp(32px,4vh,52px)]">
          <p className="font-mono text-[.6rem] tracking-[.2em] uppercase text-[#D85A30] mb-[8px]">
            ما يميّز آفاق
          </p>
          <h2 className="font-cairo font-black text-[clamp(1.6rem,3vw,2.6rem)] text-[#F7F2E9] leading-[1.1] tracking-tight">
            رحلة متكاملة — مو مجرد محتوى
          </h2>
        </div>

        <div className="grid gap-[clamp(12px,1.5vw,20px)] md:grid-cols-3">
          {[
            {
              icon: "🧠",
              title: "التطوير التقني",
              color: EMBER,
              items: [
                "Python وأدوات تحليل البيانات",
                "بناء نماذج ML وNLP",
                "نشر التطبيقات على الـ Cloud",
                "العمل مع LLMs وـ APIs",
              ],
            },
            {
              icon: "💡",
              title: "التطوير المهني",
              color: MINT,
              items: [
                "+١٠ لقاءات مع خبراء السوق",
                "بناء البورتفوليو والـ LinkedIn",
                "مهارات تقديم المشاريع",
                "فهم سوق العمل السعودي",
              ],
            },
            {
              icon: "🌟",
              title: "التطوير الشخصي",
              color: GOLD,
              items: [
                "اختبار Gallup مواطن القوة",
                "التعامل مع ضغط المشاريع",
                "مهارات القيادة والعمل الجماعي",
                "بناء الثقة بالنفس المهنية",
              ],
            },
          ].map((p) => (
            <div
              key={p.title}
              className="rounded-[14px] border border-[rgba(109,211,167,.09)] bg-[#0C1F35] p-[clamp(20px,2vw,28px)] overflow-hidden relative"
            >
              <div
                className="absolute top-0 left-0 right-0 h-[2px]"
                style={{ background: p.color }}
              />
              <span className="text-[2rem] block mb-[12px]">{p.icon}</span>
              <h3
                className="font-cairo font-bold text-[1.05rem] text-[#F7F2E9] mb-[14px]"
                style={{ color: p.color }}
              >
                {p.title}
              </h3>
              <ul className="space-y-[8px]">
                {p.items.map((i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-[.78rem] text-[#94B4C8] leading-[1.6]"
                  >
                    <span
                      className="mt-[4px] flex-shrink-0"
                      style={{ color: p.color, fontSize: ".45rem" }}
                    >
                      ◆
                    </span>
                    {i}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ══ CURRICULUM ════════════════════════════════ */}
      <section
        id="curriculum"
        className="px-[clamp(16px,5vw,80px)] py-[clamp(48px,7vh,80px)]"
      >
        {/* header */}
        <div className="flex flex-col items-center text-center mb-[clamp(32px,4vh,52px)]">
          <p className="font-mono text-[.6rem] tracking-[.2em] uppercase text-[#D85A30] mb-[8px]">
            CURRICULUM · المنهج
          </p>
          <h2 className="font-cairo font-black text-[clamp(1.6rem,3vw,2.6rem)] text-[#F7F2E9] leading-[1.1] tracking-tight mb-[8px]">
            ٥ محطات — رحلة واحدة متكاملة
          </h2>
          <p className="text-[.88rem] text-[#94B4C8] max-w-[480px] leading-[1.8]">
            كل محطة أسبوعان مكثفان — تنتهي بمشروع صغير يتراكم مع باقي المحطات
            لتشكّل مشروع تخرجك النهائي.
          </p>
        </div>

        {/* timeline line desktop */}
        <div className="space-y-[8px] max-w-[860px] mx-auto">
          {MODULES.map((m, i) => (
            <Module
              key={m.num}
              {...m}
              open={openMod === i}
              onToggle={() => setOpenMod(openMod === i ? -1 : i)}
            />
          ))}
        </div>

        {/* bundle CTA */}
        <div className="mt-[clamp(28px,4vh,44px)] max-w-[860px] mx-auto rounded-[14px] border border-[rgba(216,90,48,.2)] bg-[#0C1F35] p-[clamp(20px,2.5vw,32px)] flex flex-col md:flex-row items-start md:items-center gap-[20px] justify-between relative overflow-hidden">
          <div
            className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: `linear-gradient(90deg,${EMBER},#E8733A)` }}
          />
          <div>
            <p className="font-mono text-[.52rem] tracking-[.14em] uppercase text-[#3D6278] mb-[4px]">
              المسار الكامل
            </p>
            <p className="font-cairo font-black text-[clamp(1rem,1.5vw,1.3rem)] text-[#F7F2E9] mb-[6px]">
              سجّل في المسار كاملاً
            </p>
            <p className="text-[.78rem] text-[#94B4C8]">
              ٥ محطات · ٣٠٠+ ساعة · مشروع تخرج · شهادة معتمدة
            </p>
          </div>
          <div className="flex items-center gap-[24px] flex-shrink-0">
            <div className="text-center">
              <span className="block font-mono text-[.48rem] text-[#3D6278] tracking-[.1em] uppercase mb-[2px]">
                كل المحطات
              </span>
              <span className="font-cairo font-black text-[1.6rem] text-[#F2B949]">
                ٦٧٥٠ ر
              </span>
            </div>
            <a
              href="https://store.afaaqwetaan.com/%D8%A7%D9%84%D8%B0%D9%83%D8%A7%D8%A1-%D8%A7%D9%84%D8%A7%D8%B5%D8%B7%D9%86%D8%A7%D8%B9%D9%8A/c114156635"
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-2 font-cairo font-bold px-[22px] py-[11px] rounded-[4px] transition-all duration-200 hover:opacity-90 whitespace-nowrap"
              style={{ background: EMBER, color: "#fff", fontSize: ".88rem" }}
            >
              سجّل الآن
            </a>
          </div>
        </div>
      </section>

      {/* ══ OUTCOMES ══════════════════════════════════ */}
      <section className="px-[clamp(16px,5vw,80px)] py-[clamp(48px,7vh,80px)] border-t border-[rgba(109,211,167,.07)]">
        <div className="grid gap-[clamp(32px,5vw,64px)] lg:grid-cols-2 items-start">
          <div>
            <p className="font-mono text-[.6rem] tracking-[.2em] uppercase text-[#D85A30] mb-[10px]">
              بعد الرحلة
            </p>
            <h2 className="font-cairo font-black text-[clamp(1.6rem,3vw,2.6rem)] text-[#F7F2E9] leading-[1.1] tracking-tight mb-[16px]">
              من ستكون بعد إتمام المسار
            </h2>
            <p className="text-[.88rem] text-[#94B4C8] leading-[1.85] mb-[28px]">
              ما تخرج بشهادة حضور — تخرج بـ
              <strong className="text-[#F7F2E9]">
                {" "}
                مشروع يُثبت قدراتك
              </strong>{" "}
              وملف مهني قابل للإضافة مباشرة لـ LinkedIn وسيرتك الذاتية.
            </p>
            <ul className="space-y-[10px]">
              {[
                "تحليل بيانات حقيقية واستخلاص رؤى تجارية",
                "بناء نماذج ML وتقديمها لأصحاب القرار",
                "العمل مع نماذج اللغة الكبيرة (LLMs) وـ AraBERT",
                "ربط AI بـ KPIs الأعمال عبر Power BI وStreamlit",
                "نشر تطبيق AI كامل على الـ Cloud",
                "تقديم مشروع تخرج أمام لجنة تقييم من السوق",
              ].map((o) => (
                <Check key={o}>{o}</Check>
              ))}
            </ul>
          </div>

          {/* jobs */}
          <div>
            <p className="font-mono text-[.6rem] tracking-[.2em] uppercase text-[#3D6278] mb-[14px]">
              المسمّيات الوظيفية التي تتأهل لها
            </p>
            <div className="grid grid-cols-2 gap-[10px]">
              {[
                { title: "AI Engineer", sub: "مهندس ذكاء اصطناعي" },
                { title: "ML Engineer", sub: "مهندس تعلم الآلة" },
                { title: "Data Scientist", sub: "عالم بيانات" },
                { title: "AI Analyst", sub: "محلل بيانات بـ AI" },
                { title: "NLP Engineer", sub: "مهندس معالجة اللغة" },
                { title: "AI Product Manager", sub: "مدير منتج AI" },
              ].map((j) => (
                <div
                  key={j.title}
                  className="rounded-[10px] border border-[rgba(109,211,167,.09)] bg-[#0C1F35] p-[14px] hover:border-[rgba(216,90,48,.2)] transition-colors duration-200"
                >
                  <span className="block font-mono text-[.7rem] font-bold text-[#D85A30] mb-[2px]">
                    {j.title}
                  </span>
                  <span className="block text-[.72rem] text-[#3D6278]">
                    {j.sub}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ WHO IS IT FOR ════════════════════════════ */}
      <section className="px-[clamp(16px,5vw,80px)] py-[clamp(48px,7vh,80px)] border-t border-[rgba(109,211,167,.07)]">
        <div className="text-center mb-[clamp(32px,4vh,48px)]">
          <p className="font-mono text-[.6rem] tracking-[.2em] uppercase text-[#D85A30] mb-[8px]">
            لمن هذا المسار؟
          </p>
          <h2 className="font-cairo font-black text-[clamp(1.6rem,3vw,2.6rem)] text-[#F7F2E9] leading-[1.1] tracking-tight">
            آفاق مو للكل — هي لك إذا…
          </h2>
        </div>
        <div className="grid gap-[clamp(12px,1.5vw,18px)] md:grid-cols-3 max-w-[860px] mx-auto">
          {FOR_WHO.map((f) => (
            <div
              key={f.title}
              className="rounded-[14px] border border-[rgba(109,211,167,.09)] bg-[#0C1F35] p-[clamp(18px,2vw,26px)]"
            >
              <span className="text-[2.2rem] block mb-[10px]">{f.icon}</span>
              <h3 className="font-cairo font-bold text-[.98rem] text-[#F7F2E9] mb-[8px]">
                {f.title}
              </h3>
              <p className="text-[.78rem] text-[#94B4C8] leading-[1.7]">
                {f.desc}
              </p>
            </div>
          ))}
        </div>

        {/* not for */}
        <div className="mt-[20px] max-w-[860px] mx-auto rounded-[10px] border border-[rgba(247,242,233,.07)] bg-[#0C1F35] p-[clamp(14px,1.8vw,20px)] flex items-start gap-3">
          <span className="text-[1.1rem] flex-shrink-0 mt-[2px]">⚠️</span>
          <p className="text-[.78rem] text-[#3D6278] leading-[1.7]">
            <strong className="text-[#94B4C8]">آفاق مو للكل:</strong> إذا كنت
            تبحث عن محتوى تشوفه بدون التزام أو مشاريع، هناك دورات مسجّلة تناسبك
            أكثر. هذا المسار مصمّم للشخص الجاد اللي يريد تحولاً حقيقياً.
          </p>
        </div>
      </section>

      {/* ══ PROJECTS ══════════════════════════════════ */}
      <section className="px-[clamp(16px,5vw,80px)] py-[clamp(48px,7vh,80px)] border-t border-[rgba(109,211,167,.07)]">
        <div className="text-center mb-[clamp(28px,3.5vh,44px)]">
          <p className="font-mono text-[.6rem] tracking-[.2em] uppercase text-[#D85A30] mb-[8px]">
            مشاريع الخريجين
          </p>
          <h2 className="font-cairo font-black text-[clamp(1.5rem,2.5vw,2.2rem)] text-[#F7F2E9] leading-[1.1] tracking-tight">
            مشاريع حطّت بأمان
          </h2>
        </div>
        <div className="grid gap-[clamp(10px,1.2vw,14px)] sm:grid-cols-2 lg:grid-cols-4">
          {PROJECTS.map((p) => (
            <div
              key={p.code}
              className="rounded-[12px] border border-[rgba(109,211,167,.09)] bg-[#0C1F35] p-[18px] hover:border-[rgba(216,90,48,.2)] transition-colors duration-200 overflow-hidden relative"
            >
              <div
                className="absolute top-0 left-0 right-0 h-[2px]"
                style={{ background: EMBER }}
              />
              <span className="font-mono text-[.56rem] tracking-[.1em] text-[#3D6278] block mb-[6px]">
                AFQ → {p.code}
              </span>
              <h4 className="font-cairo font-black text-[1rem] text-[#D85A30] mb-[6px]">
                {p.name}
              </h4>
              <p className="text-[.74rem] text-[#94B4C8] leading-[1.65]">
                {p.desc}
              </p>
              <div className="mt-[10px] inline-flex items-center gap-1.5 text-[.6rem] font-mono text-[#6DD3A7]">
                <span>✓</span>
                <span>LANDED SAFELY</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══ PRICING ═══════════════════════════════════ */}
      <section className="px-[clamp(16px,5vw,80px)] py-[clamp(48px,7vh,80px)] border-t border-[rgba(109,211,167,.07)]">
        <div className="text-center mb-[clamp(32px,4vh,52px)]">
          <p className="font-mono text-[.6rem] tracking-[.2em] uppercase text-[#D85A30] mb-[8px]">
            السعر والتسجيل
          </p>
          <h2 className="font-cairo font-black text-[clamp(1.6rem,3vw,2.6rem)] text-[#F7F2E9] leading-[1.1] tracking-tight">
            احجز تذكرتك
          </h2>
        </div>

        <div className="grid gap-[clamp(12px,1.5vw,18px)] md:grid-cols-2 max-w-[720px] mx-auto">
          {/* single module */}
          <div className="rounded-[14px] border border-[rgba(109,211,167,.09)] bg-[#0C1F35] p-[clamp(20px,2.5vw,30px)] flex flex-col">
            <p className="font-mono text-[.52rem] tracking-[.14em] uppercase text-[#3D6278] mb-[4px]">
              محطة واحدة
            </p>
            <p className="font-cairo font-black text-[1.05rem] text-[#F7F2E9] mb-[12px]">
              أي محطة تختارها
            </p>
            <div className="flex items-baseline gap-2 mb-[16px]">
              <span className="font-cairo font-black text-[2.4rem] text-[#F2B949] leading-none">
                ١٣٥٠
              </span>
              <span className="text-[#94B4C8] text-[.82rem]">
                ر · محطة واحدة
              </span>
            </div>
            <ul className="space-y-[6px] mb-[20px] flex-1">
              {[
                "٦٠ ساعة تدريبية",
                "تسجيلات الجلسات",
                "مشروع محطة",
                "شهادة إتمام المحطة",
              ].map((i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 text-[.76rem] text-[#94B4C8]"
                >
                  <span className="text-[#D85A30] text-[.45rem]">◆</span>
                  {i}
                </li>
              ))}
            </ul>
            <a
              href="https://store.afaaqwetaan.com/%D8%A7%D9%84%D8%B0%D9%83%D8%A7%D8%A1-%D8%A7%D9%84%D8%A7%D8%B5%D8%B7%D9%86%D8%A7%D8%B9%D9%8A/c114156635"
              target="_blank"
              rel="noopener"
              className="flex items-center justify-center gap-2 font-cairo font-bold py-[11px] rounded-[4px] border border-[rgba(216,90,48,.3)] text-[#D85A30] transition-all duration-200 hover:bg-[rgba(216,90,48,.1)] text-[.88rem]"
            >
              اختر المحطة
            </a>
          </div>

          {/* full track — featured */}
          <div className="rounded-[14px] border border-[rgba(216,90,48,.3)] bg-[#0C1F35] p-[clamp(20px,2.5vw,30px)] flex flex-col relative overflow-hidden">
            <div
              className="absolute top-0 left-0 right-0 h-[3px]"
              style={{ background: `linear-gradient(90deg,${EMBER},#E8733A)` }}
            />
            <div className="absolute top-[16px] left-[16px]">
              <span className="font-mono text-[.46rem] tracking-[.1em] uppercase bg-[rgba(216,90,48,.15)] border border-[rgba(216,90,48,.3)] text-[#D85A30] px-[8px] py-[3px] rounded-full">
                الأفضل قيمة
              </span>
            </div>
            <p className="font-mono text-[.52rem] tracking-[.14em] uppercase text-[#3D6278] mb-[4px] mt-[8px]">
              المسار الكامل
            </p>
            <p className="font-cairo font-black text-[1.05rem] text-[#F7F2E9] mb-[12px]">
              ٥ محطات متسلسلة
            </p>
            <div className="flex items-baseline gap-2 mb-[4px]">
              <span className="font-cairo font-black text-[2.4rem] text-[#F2B949] leading-none">
                ٦٧٥٠
              </span>
              <span className="text-[#94B4C8] text-[.82rem]">ر</span>
            </div>
            <p className="text-[.68rem] text-[#3D6278] mb-[16px]">
              أو بالتقسيط عبر تمارا
            </p>
            <ul className="space-y-[6px] mb-[20px] flex-1">
              {[
                "٣٠٠+ ساعة تدريبية",
                "لقاءات أسبوعية مع خبراء السوق",
                "مشروع تخرج كامل",
                "شهادة إتمام المسار المعتمدة",
                "مجتمع خريجي آفاق",
              ].map((i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 text-[.76rem] text-[#94B4C8]"
                >
                  <span className="text-[#D85A30] text-[.45rem]">◆</span>
                  {i}
                </li>
              ))}
            </ul>
            <a
              href="https://store.afaaqwetaan.com/%D8%A7%D9%84%D8%B0%D9%83%D8%A7%D8%A1-%D8%A7%D9%84%D8%A7%D8%B5%D8%B7%D9%86%D8%A7%D8%B9%D9%8A/c114156635"
              target="_blank"
              rel="noopener"
              className="flex items-center justify-center gap-2 font-cairo font-bold py-[12px] rounded-[4px] transition-all duration-200 hover:opacity-90 text-[.9rem]"
              style={{ background: EMBER, color: "#fff" }}
            >
              <svg
                className="w-4 h-4 flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
              سجّل في المسار
            </a>
          </div>
        </div>

        {/* tamara note */}
        <p className="text-center mt-[16px] text-[.72rem] text-[#3D6278] font-mono tracking-[.06em]">
          ◆ الدفع بالتقسيط متاح عبر تمارا — يظهر الخيار تلقائياً عند الدفع في
          سلة
        </p>
      </section>

      {/* ══ FAQ ═══════════════════════════════════════ */}
      <section className="px-[clamp(16px,5vw,80px)] py-[clamp(48px,7vh,80px)] border-t border-[rgba(109,211,167,.07)]">
        <div className="text-center mb-[clamp(28px,3.5vh,44px)]">
          <p className="font-mono text-[.6rem] tracking-[.2em] uppercase text-[#D85A30] mb-[8px]">
            الأسئلة الشائعة
          </p>
          <h2 className="font-cairo font-black text-[clamp(1.5rem,2.5vw,2.2rem)] text-[#F7F2E9] leading-[1.1] tracking-tight">
            كل سؤال في ذهنك
          </h2>
        </div>
        <div className="max-w-[720px] mx-auto space-y-[8px]">
          {FAQS.map((f) => (
            <FAQItem key={f.q} {...f} />
          ))}
        </div>
      </section>

      {/* ══ FINAL CTA ═════════════════════════════════ */}
      <section className="px-[clamp(16px,5vw,80px)] py-[clamp(56px,8vh,96px)] border-t border-[rgba(109,211,167,.08)] relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 50% 100%,rgba(216,90,48,0.08) 0%,transparent 65%)",
          }}
        />
        <div className="relative z-10 text-center max-w-[580px] mx-auto">
          <span className="inline-flex items-center gap-2 mb-[20px]">
            <span
              className="w-[6px] h-[6px] rounded-full animate-pulse"
              style={{ background: EMBER }}
            />
            <span className="font-mono text-[.6rem] tracking-[.18em] uppercase text-[#D85A30]">
              BOARDING NOW · التسجيل مفتوح
            </span>
            <span
              className="w-[6px] h-[6px] rounded-full animate-pulse"
              style={{ background: EMBER }}
            />
          </span>
          <h2 className="font-cairo font-black text-[clamp(1.8rem,4vw,3.2rem)] text-[#F7F2E9] leading-[1.08] tracking-tight mb-[14px]">
            جاهز تبدأ
            <br />
            <em className="not-italic" style={{ color: EMBER }}>
              رحلتك؟
            </em>
          </h2>
          <p className="text-[.9rem] text-[#94B4C8] leading-[1.85] mb-[32px]">
            المقاعد محدودة لضمان جودة التجربة. سجّل الآن وابدأ رحلتك نحو سوق
            العمل.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href="https://store.afaaqwetaan.com/%D8%A7%D9%84%D8%B0%D9%83%D8%A7%D8%A1-%D8%A7%D9%84%D8%A7%D8%B5%D8%B7%D9%86%D8%A7%D8%B9%D9%8A/c114156635"
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-2 font-cairo font-bold px-[32px] py-[14px] rounded-[4px] text-white transition-all duration-200 hover:opacity-90 hover:-translate-y-[1px]"
              style={{
                background: EMBER,
                fontSize: "clamp(.9rem,.98rem,1.05rem)",
              }}
            >
              <svg
                className="w-4 h-4 flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
              سجّل في مسار الذكاء الاصطناعي
            </a>
            <a
              href="mailto:afaaq@wetaan.com"
              className="inline-flex items-center gap-2 font-cairo font-medium px-[24px] py-[14px] rounded-[4px] border border-[rgba(247,242,233,.15)] text-[#94B4C8] transition-all duration-200 hover:text-[#F7F2E9] hover:border-[rgba(247,242,233,.3)]"
              style={{ fontSize: "clamp(.86rem,.92rem,.96rem)" }}
            >
              تواصل معنا
            </a>
          </div>
          <div className="flex items-center justify-center gap-[clamp(12px,2vw,24px)] mt-[24px] flex-wrap">
            {[
              "دفع بالتقسيط عبر تمارا",
              "تدريب أونلاين",
              "شهادة معتمدة",
              "مجتمع مستمر بعد التخرج",
            ].map((t) => (
              <span
                key={t}
                className="flex items-center gap-1.5 font-mono text-[.56rem] text-[#3D6278] tracking-[.06em]"
              >
                <span className="text-[.42rem]" style={{ color: MINT }}>
                  ◆
                </span>
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
